import { CircleAlert, CircleCheck, Info, Loader2 } from "lucide-react";
import { match } from "ts-pattern";

import { RichTextUploadQueue } from "~/components/RichText/RichTextUploadQueue";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastProps,
} from "~/components/ui/toast";
import { useToast } from "~/components/ui/use-toast";
import { IS_MONO_DESIGN } from "~/config/designVariant";
import { useRichTextUploadQueue } from "~/hooks/useRichTextUploadQueue";
import { cn } from "~/lib/utils";

import { TOAST_HANDLES } from "../../../e2e/data/common/handles";
import { Icon } from "../Icon";

type ToastIconProps = {
  variant: ToastProps["variant"];
};

function ToastIcon({ variant }: ToastIconProps) {
  if (IS_MONO_DESIGN) {
    return match(variant)
      .with("success", () => <CircleCheck className="mr-2 size-5 shrink-0 text-success-500" />)
      .with("loading", () => (
        <Loader2 className="mr-2 size-5 shrink-0 animate-spin text-primary-500" />
      ))
      .with("destructive", () => <CircleAlert className="mr-2 size-5 shrink-0 text-error-500" />)
      .otherwise(() => <Info className="mr-2 size-5 shrink-0 text-primary-700" />);
  }

  return match(variant)
    .with("success", () => (
      <Icon name="InputRoundedMarkerSuccess" className="mr-2 size-4 text-green-500 shrink-0" />
    ))
    .with("loading", () => <Loader2 className="mr-2 size-4 animate-spin text-blue-500 shrink-0" />)
    .otherwise(() => null);
}

export function Toaster() {
  const { toasts } = useToast();
  const { items, clearFinished, remove } = useRichTextUploadQueue();

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        // Callers pass a single message as either title or description, so in mono both look the
        // same on their own and only split into a heading and body when the toast has both.
        const hasTitleAndDescription = Boolean(title && description);

        return (
          <Toast key={id} data-testid={TOAST_HANDLES.ROOT} {...props}>
            <div className="flex items-center gap-1 mono:items-start">
              <ToastIcon variant={props.variant} />
              <div className="flex flex-col">
                {title && (
                  <ToastTitle
                    data-testid={TOAST_HANDLES.TITLE}
                    className={cn({ "mono:text-base mono:leading-5": hasTitleAndDescription })}
                  >
                    {title}
                  </ToastTitle>
                )}
                {description && (
                  <ToastDescription
                    data-testid={TOAST_HANDLES.DESCRIPTION}
                    className={cn({
                      "mono:font-control": hasTitleAndDescription,
                      "mono:font-semibold": !title,
                    })}
                  >
                    {description}
                  </ToastDescription>
                )}
              </div>
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport
        data-testid={TOAST_HANDLES.VIEWPORT}
        queueSlot={
          <RichTextUploadQueue
            items={items}
            onClearFinished={clearFinished}
            onRemoveItem={remove}
          />
        }
      />
    </ToastProvider>
  );
}
